<template>
  <div v-if="!loading">
    <div class="section">
      <!-- Main header section -->
      <div class="section-heading typo-small-title">
        <SetSvg
          v-if="isSet"
          class="icon icon--small section-icon"
        />
        <RegionSvg
          v-if="!isSet"
          class="icon icon--small section-icon"
        />
        <span>{{ mainHeaderTitle }}</span>
        <router-link
          v-if="hasRisk"
          class="section-link"
          :to="seeAllRoute"
        >
          {{ $t('general.see_all') }}
          <UseSvg
            class="icon icon--small"
            name="arrow_right_16"
          />
        </router-link>
      </div>

      <!-- Body section -->
      <div
        v-if="regionList"
        class="section-content section-content--no-padding"
      >
        <!-- Empty message section -->
        <div
          v-if="!hasRisk"
          class="section-empty typo-small-title"
        >
          <div class="section-empty-title">
            <span>{{ noRiskTitle }}</span>
          </div>
          <div class="section-empty-message">
            <span>{{ noRiskDescription }}</span>
          </div>
        </div>
        <!-- Inner header section -->
        <div
          v-if="showInnerHeader"
          class="regions-heading typo-smallish"
        >
          <div class="regions-heading-left">
            <span>{{ innerHeaderTitle }}</span>
          </div>
          <div class="regions-heading-right">
            {{ innerHeaderDescription }}
          </div>
        </div>
        <!-- Region list -->
        <div
          v-if="hasRisk"
          class="regions"
        >
          <router-link
            v-for="region in regionList"
            :key="region.id || region.set_number"
            class="region"
            :to="getRoute(region)"
          >
            <template v-if="region?.bounds">
              <PolygonSvg
                :bounds="region.bounds"
                :class="`region-thumbnail region-thumbnail--${regionType(region)}`"
                :size="20"
              />
            </template>
            <div class="typo-small-title region-title">
              {{ region?.name_with_parent_name || region?.name || region.set_number }}
            </div>
            <div class="region-risk">
              {{ region.issues_count }}
            </div>
          </router-link>
          <div
            v-if="regionList.length === 0"
            class="no-regions typo-small-title"
          >
            {{ noRiskItemText }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <Loader
    v-else
    class="section-loader"
  />
</template>

<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import useAjax from '@/functions/use_ajax';
import useOrganisationProfile from '@/functions/use_organisation_profile'; 
import I18n from 'i18n-js';

import Loader from '@/components/generic/loader.vue';

import RegionSvg from '@/images/region.svg';
import SetSvg from '@/images/set_icon.svg';
import PolygonSvg from '@/components/generic/polygon_svg.vue';
import UseSvg from '@/components/generic/use_svg.vue';

const props = defineProps({
  isStation: { type: Boolean, required: false, default: false },
  isSet: { type: Boolean, required: false, default: false },
  isHme: { type: Boolean, required: false, default: false },
  isPlant: { type: Boolean, required: false, default: false },
  regionsPathName: { type: String, required: false, default: '' },
  highRiskRegionsEndpoint: { type: String, required: true },
});

const ajax = useAjax();
const loading = ref(true);

const { showRisk } = useOrganisationProfile();

const highRiskRegions = ref(null);

const hasRisk = computed(() => {
  if (props.isSet) {
    return highRiskRegions.value?.has_sets
  } else if (props.isHme) {
    return highRiskRegions.value?.has_heavy_mobile_equipments;
  } else {
    return highRiskRegions.value?.has_regions;
  }
});

const regionList = computed(() => {
  if (props.isSet) {
    return highRiskRegions.value?.high_risk_sets
  } else if (props.isHme) {
    return highRiskRegions.value?.high_risk_heavy_mobile_equipments;
  } else {
    return highRiskRegions.value?.high_risk_regions;
  }
})

const mainHeaderTitle = computed(() => {
  if (props.isStation) return I18n.t('shafts.issues.stations_issues');
  if (props.isSet) return I18n.t('shafts.issues.sets_issues');
  if (props.isHme) return I18n.t('shafts.issues.heavy_mobile_equipment_issues');
  return I18n.t('nav.issues');
});

const issueType = computed(() => {
  if (props.isStation) return 'stations';
  if (props.isSet) return 'sets';
  if (props.isHme) return 'heavy_mobile_equipment';
  if (props.isPlant) return 'plant';
  return 'ship';
});

const seeAllRoute = computed(() => {
  return { name: props.regionsPathName, params: { issueType: issueType.value } };
});

const innerHeaderTitle = computed(() => {
  if (props.isSet) return I18n.t('shafts.issues.sets_issues');
  if (props.isHme) return I18n.t('shafts.configuration.heavy_mobile_equipment.new');
  return I18n.t('operations.region')
});

const innerHeaderDescription = computed(() => {
  if (showRisk.value) return I18n.t('operations.high_risk_issues');
  return I18n.t('operations.high_priority_issues');
});

const noRiskTitle = computed(() => {
  if (props.isSet) return I18n.t('operations.sets_empty_title');
  if (props.isHme) return I18n.t('operations.hme_empty_title');
  return I18n.t('operations.regions_empty_title');l
});

const noRiskDescription = computed(() => {
  if (props.isSet) return I18n.t('operations.sets_empty_message');
  return I18n.t('operations.regions_empty_message');
});

const noRiskItemText = computed(() => {
  if (showRisk.value) {
    if (props.isSet) return I18n.t('operations.no_high_risk_set_issues');
    if (props.isHme) return I18n.t('operations.no_high_risk_hme_issues');
    return I18n.t('operations.no_high_risk_issues');
  } else {
    if (props.isSet) return I18n.t('operations.no_high_priority_set_issues');
    if (props.isHme) return I18n.t('operations.no_high_priority_hme_issues');
    return I18n.t('operations.no_high_priority_issues');
  }
});

const showInnerHeader = computed(() => {
  return hasRisk.value && regionList.value.length > 0;
});

function getRoute(region) {
  if (props.isSet) return { name: props.regionsPathName, params: { issueType: 'sets', setNumber: region.set_number } };
  if (props.isStation) return { name: props.regionsPathName, params: { issueType: 'stations', stationId: region.station_id }, query: { regionId: region.id } };
  if (props.isHme) return { name: props.regionsPathName, params: { issueType: 'heavy_mobile_equipment', heavyMobileEquipmentId: region.id } };
  if (props.isPlant) return { name: props.regionsPathName, params: { issueType: 'plant' }, query: { regionId: region.id } };
  return { name: props.regionsPathName, params: { issueType: 'ship' }, query: { regionId: region.id } };
}

/**
 * Checks if a region type is available
 * otherwise sets it to default
 */
function regionType(region) {
  if (Object.prototype.hasOwnProperty.call(region, 'region_type')) {
    return region.region_type;
  } else {
    return 'default';
  }
}

onMounted(async() => {
  await loadOperation();
});

watch(props, async () => await loadData());

/**
 * Get operation data (Will have a refactor ticket to move the APIs to the parent components)
 */
async function loadOperation() {
  loading.value = true;
  let { ok, data } = await ajax(props.highRiskRegionsEndpoint);

  if (ok) {
    highRiskRegions.value = data;
  }
  loading.value = false;
}
</script>
<style lang="scss">
@import '@/stylesheets/global';

.section {
  background-color: map-get($background-color, dark);
  border-radius: map-get($border-radius, mega);
  margin-bottom: bu(1.5);
  overflow: hidden;
}

.section-heading {
  display: flex;
  align-items: center;
  width: 100%;
  padding: bu(1.5);
  border-bottom: 1px solid map-get($background-color, default);
  font-weight: 500;
}

.section-icon {
  margin-right: bu(1.5);
  color: map-get($foreground-color, dark);
}

.set-icon {
  margin-right: bu(1.5);
  color: map-get($foreground-color, dark);
}

.section-link {
  margin-left: auto;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  color: map-get($foreground-color, dark);
  font-weight: 300;
}

.section-content {
  padding: bu(1);

  &--no-padding {
    padding: 0;
  }
}

.section-empty {
  padding: bu(2);
  color: map-get($foreground-color, dark);
}

.section-empty-title {
  margin-bottom: bu(1);
  font-weight: 500;
}

.regions-heading {
  border-bottom: 1px solid map-get($background-color, default);
  display: flex;
  color: map-get($foreground-color, dark);
  padding: bu(0.75) bu(2);
}

.regions-heading-left {
  margin-right: auto;
}

.regions {
  padding: 0 bu(2) bu(1);
}

.region {
  display: flex;
  align-items: center;
  padding: bu(1) 0;

  & + & {
    border-top: 1px solid map-get($background-color, default);
  }
}

.region-thumbnail {
  stroke-width: 1.5px;
  margin-right: bu(0.75);
  flex-shrink: 0;

  @each $type, $color in $region-color {
    &--#{$type} {
      stroke: map-get($color, light);
      fill: transparentize(map-get($color, dark), 0.8);
    }
  }
}

.region-title {
  @include text-overflow;
  margin-right: auto;
  padding: bu(0.75);
}

.region-risk {
  text-align: right;
  display: flex;
  align-items: center;

  &::before {
    content: '';
    display: block;
    width: bu(1.25);
    height: bu(1.25);
    margin-right: bu(0.5);
    border-radius: map-get($border-radius, circle);
    background-color: map-deep-get($risk-rating-color, high, default);
    flex-shrink: 0;
  }
}

.no-regions {
  margin: bu(2) 0 bu(1.5);
  color: map-get($foreground-color, dark);
}

.section-loader {
  min-height: rem(150);
}
</style>
